import { useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listMessagingUser } from "@rubi/api-generated/clientRs";
import type { MessagingUser } from "@rubi/api-generated/clientRs";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  orgId: string;
  selectedUserId: string | undefined;
  onSelectUser: (userId: string) => void;
}

function initialsFor(user: MessagingUser): string {
  const first = user.first_name?.trim()?.[0] ?? "";
  const last = user.last_name?.trim()?.[0] ?? "";
  const combined = `${first}${last}`.toUpperCase();
  return combined || user.email.slice(0, 2).toUpperCase();
}

function matchesSearch(user: MessagingUser, term: string): boolean {
  if (!term) return true;
  const haystack =
    `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase();
  return haystack.includes(term.toLowerCase());
}

const MessagingUsersList = ({ orgId, selectedUserId, onSelectUser }: Props) => {
  const { t } = useTranslation("message");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, refetch } = useQuery(
    ["messagingUsers", orgId],
    async () => {
      const response = await listMessagingUser({
        query: { rubi_org_id: orgId },
        throwOnError: true,
      });
      return response.data?.messaging_users ?? [];
    },
    { staleTime: 60_000 },
  );

  const filteredUsers = useMemo(
    () => (data ?? []).filter((u) => matchesSearch(u, search.trim())),
    [data, search],
  );

  return (
    <>
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t("allHistory.messagingUsers")}
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={t("allHistory.searchUsers")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  {t("allHistory.retry")}
                </Button>
              }
            >
              {t("allHistory.loadFailedUsers")}
            </Alert>
          </Box>
        )}
        {!isLoading && !isError && filteredUsers.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("allHistory.noUsers")}
            </Typography>
          </Box>
        )}
        <Stack>
          {filteredUsers.map((user) => {
            const isSelected = user.id === selectedUserId;
            return (
              <Box
                key={user.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectUser(user.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectUser(user.id);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: isSelected ? "action.selected" : "transparent",
                  borderLeft: isSelected
                    ? "3px solid"
                    : "3px solid transparent",
                  borderLeftColor: isSelected ? "primary.main" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "action.selected" : "action.hover",
                  },
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: "grey.900",
                    color: "common.white",
                    width: 40,
                    height: 40,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {initialsFor(user)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t("allHistory.conversationsCount", {
                      count: user.conversation_count,
                    })}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </>
  );
};

export default MessagingUsersList;
